"use client";

import CodeMirror, {
  EditorView,
  keymap,
  type Extension,
} from "@uiw/react-codemirror";
import { SQLite, sql } from "@codemirror/lang-sql";
import { cn } from "@ocr-monorepo/ui/lib/utils";

type SqlCodeEditorProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  height?: string;
  ariaLabel?: string;
  readOnly?: boolean;
  disabled?: boolean;
};

type SqlSingleLineInputProps = Omit<SqlCodeEditorProps, "height"> & {
  onSubmit?: () => void;
};

const sqlExtensions = [sql({ dialect: SQLite })];

const editorTheme = EditorView.theme({
  "&": {
    backgroundColor: "var(--card)",
    color: "var(--foreground)",
    fontSize: "0.875rem",
    height: "100%",
  },
  ".cm-scroller": {
    fontFamily:
      "var(--font-geist-mono), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    lineHeight: "1.5rem",
  },
  ".cm-content": {
    caretColor: "var(--primary)",
    padding: "0.75rem",
  },
  ".cm-gutters": {
    backgroundColor: "color-mix(in oklch, var(--muted) 35%, transparent)",
    color: "var(--muted-foreground)",
    borderRight: "1px solid var(--border)",
  },
  ".cm-activeLine": {
    backgroundColor: "color-mix(in oklch, var(--muted) 32%, transparent)",
  },
  ".cm-activeLineGutter": {
    backgroundColor: "color-mix(in oklch, var(--muted) 48%, transparent)",
  },
  ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
    backgroundColor: "color-mix(in oklch, var(--primary) 24%, transparent)",
  },
  "&.cm-focused": {
    outline: "none",
  },
});

const singleLineTheme = EditorView.theme({
  "&": {
    height: "2.25rem",
  },
  ".cm-scroller": {
    overflow: "hidden",
  },
  ".cm-content": {
    padding: "0.375rem 0.625rem",
    whiteSpace: "pre",
  },
  ".cm-line": {
    padding: 0,
  },
  ".cm-gutters": {
    display: "none",
  },
});

function sanitizeSingleLine(value: string) {
  return value.replace(/\s*\r?\n\s*/g, " ").trimStart();
}

function editorClassName(className?: string) {
  return cn(
    "overflow-hidden rounded-md border border-border bg-card text-foreground transition-colors focus-within:border-primary",
    className,
  );
}

export function SqlCodeEditor({
  value,
  onChange,
  className,
  placeholder,
  height = "11rem",
  ariaLabel = "SQL editor",
  readOnly,
  disabled,
}: SqlCodeEditorProps) {
  return (
    <CodeMirror
      value={value}
      height={height}
      placeholder={placeholder}
      editable={!disabled && !readOnly}
      readOnly={readOnly}
      basicSetup={{
        foldGutter: false,
        highlightActiveLine: true,
        highlightActiveLineGutter: true,
      }}
      extensions={[...sqlExtensions, editorTheme, EditorView.lineWrapping]}
      theme="none"
      indentWithTab={false}
      onChange={onChange}
      aria-label={ariaLabel}
      className={editorClassName(className)}
    />
  );
}

export function SqlSingleLineInput({
  value,
  onChange,
  onSubmit,
  className,
  placeholder,
  ariaLabel = "SQL input",
  readOnly,
  disabled,
}: SqlSingleLineInputProps) {
  const submitKeymap: Extension = keymap.of([
    {
      key: "Enter",
      run: () => {
        onSubmit?.();
        return true;
      },
    },
    {
      key: "Shift-Enter",
      run: () => true,
    },
  ]);

  return (
    <CodeMirror
      value={value}
      height="2.25rem"
      placeholder={placeholder}
      editable={!disabled && !readOnly}
      readOnly={readOnly}
      basicSetup={{
        foldGutter: false,
        lineNumbers: false,
        highlightActiveLine: false,
        highlightActiveLineGutter: false,
      }}
      extensions={[...sqlExtensions, editorTheme, singleLineTheme, submitKeymap]}
      theme="none"
      indentWithTab={false}
      onChange={(nextValue) => onChange(sanitizeSingleLine(nextValue))}
      aria-label={ariaLabel}
      className={editorClassName(className)}
    />
  );
}
